import type { AiSource } from '../types';

/**
 * Canned material for the AI assistant panel.
 *
 * Nothing here talks to a model. The point of the mock is the shape of the
 * conversation — what a question looks like, what an answer looks like, what a
 * past session looks like — so the panel can be judged before there is
 * anything behind it. When a real endpoint arrives it replaces `answer` and
 * `SESSIONS`, and the component does not change.
 */

export interface AiFact {
  label: string;
  value: string;
  tone?: 'tag' | 'on' | 'off';
}

export interface AiMessage {
  id: number;
  from: 'user' | 'assistant';
  text: string;

  /** The small readout some answers carry under the sentence. */
  facts?: AiFact[];

  /** True when this came from the canned set rather than from the model. */
  mocked?: boolean;

  /** The document excerpts a model answer cited, in the order they were numbered. */
  sources?: AiSource[];

  /** The reference articles a model answer was given, numbered [K1], [K2]. */
  knowledge?: { id: number; title: string }[];
}

export interface AiSession {
  id: string;
  day: string;
  title: string;
}

export const QUICK_QUESTIONS = [
  'What is the current operation status?',
  'Show me the latest depth and progress',
  'Are there any anomalies?',
  'Summarize this well.',
];

/** Past conversations, newest first. */
export const SESSIONS: AiSession[] = [
  { id: 's-6', day: 'SEP 17', title: 'Hi' },
  { id: 's-5', day: 'SEP 17', title: 'Tell me about the nearest well' },
  { id: 's-4', day: 'SEP 16', title: 'Why is NPT up this week?' },
  { id: 's-3', day: 'SEP 15', title: 'Compare planned against actual depth' },
  { id: 's-2', day: 'SEP 12', title: 'Which reports are missing for DDR 78?' },
  { id: 's-1', day: 'SEP 09', title: 'Summarize the last casing run' },
];

export function greeting(name: string): AiMessage {
  return {
    id: 0,
    from: 'assistant',
    text: `Hi ${name}, I'm your AI Assistant. You can ask me about this well's data, operations, or information in this dashboard.`,
  };
}

/**
 * Pick an answer by keyword. A real assistant would not work this way; a mock
 * that always says the same thing would not show anything either.
 */
export function answer(question: string): Omit<AiMessage, 'id'> {
  const q = question.toLowerCase();

  if (q.includes('status') || q.includes('operation') || q.includes('activity')) {
    return {
      from: 'assistant',
      text: 'The current activity is Tripping Out. Pumping and Rotating are active, while On Bottom and In Slips are inactive.',
      facts: [
        { label: 'Current Activity', value: 'Tripping Out', tone: 'tag' },
        { label: 'Pumping', value: 'Active', tone: 'on' },
        { label: 'Rotating', value: 'Active', tone: 'on' },
        { label: 'On Bottom', value: 'Inactive', tone: 'off' },
        { label: 'In Slips', value: 'Inactive', tone: 'off' },
      ],
    };
  }

  if (q.includes('depth') || q.includes('progress') || q.includes('rop')) {
    return {
      from: 'assistant',
      text: 'The well is at 2,673.00 m MD and 2,672.09 m TVD. There has been no progress in the last 24 hours — the hole is on wireline, so ROP reads zero.',
      facts: [
        { label: 'Hole depth (MD)', value: '2,673.00 m' },
        { label: 'TVD', value: '2,672.09 m' },
        { label: 'Progress today', value: '0.00 m' },
        { label: 'Days on location', value: '82.04 of 70.08 est.' },
      ],
    };
  }

  if (q.includes('anomal') || q.includes('npt') || q.includes('problem') || q.includes('issue')) {
    return {
      from: 'assistant',
      text: 'Nothing was flagged in the last 24 hours. Cumulative non-productive time stands at 117.50 hours, none of it added today — the largest single contributor is still the week 6 BOP repair.',
      facts: [
        { label: 'Daily NPT', value: '0.00 hr', tone: 'on' },
        { label: 'Cumulative NPT', value: '117.50 hr' },
      ],
    };
  }

  if (q.includes('summar') || q.includes('overview') || q.includes('this well')) {
    return {
      from: 'assistant',
      text: 'Tapis Deep-1RDR1 is an exploration well on NAGA-2 in block PM515, drilled by PCSB. It spudded on 27/06/2026, is on day 84.25 against an estimate of 70.08, and has spent 83.32% of its AFE. The current phase is the full BOP test that began this morning.',
      facts: [
        { label: 'Field / Platform', value: 'TAPIS DEEP-1RDR1' },
        { label: 'Rig', value: 'NAGA-2' },
        { label: 'Objective', value: 'Exploration' },
        { label: 'Expenditure', value: '83.32% of AFE' },
      ],
    };
  }

  if (q.includes('cost') || q.includes('afe') || q.includes('budget')) {
    return {
      from: 'assistant',
      text: 'Daily cost is USD 317,280.98 and the cumulative spend is USD 42,496,417.19 against an AFE of USD 51,003,373.38 — 83.32% used at 84.25 days.',
    };
  }

  if (q.includes('hi') || q.includes('hello') || q.includes('hey')) {
    return {
      from: 'assistant',
      text: 'Hello. Ask me about the current operation, the depth and progress, costs, or any document filed against this well.',
    };
  }

  return {
    from: 'assistant',
    text: 'I do not have that yet — this assistant is a mock, so it only answers about the current operation, depth and progress, anomalies, cost, or a summary of the well.',
  };
}
